import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  TextArea,
  Input,
  Select,
  FormDialog,
  Toast,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsBoxArrowUpRight } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { usePppAiContext } from "../hooks/usePppAiContext";
import { useAuthUser } from "../hooks/useAuthUser";
import DocumentiTab from "../components/DocumentiTab";
import Fase10Tab from "../components/Fase10Tab";
import Fase11Tab from "../components/Fase11Tab";
import Fase12Tab from "../components/Fase12Tab";
import Fase13Tab from "../components/Fase13Tab";
import Fase14Tab from "../components/Fase14Tab";

const STATO_CFG = {
  ricevuta:                    { cls: "bg-blue-100 text-blue-700" },
  in_valutazione:              { cls: "bg-yellow-100 text-yellow-700" },
  interesse_pubblico_dichiarato: { cls: "bg-teal-100 text-teal-700" },
  ammessa:                     { cls: "bg-green-100 text-green-700" },
  respinta:                    { cls: "bg-red-100 text-red-700" },
  ritirata:                    { cls: "bg-gray-100 text-gray-500" },
};

const NARS_STATO_CFG = {
  non_richiesto: { cls: "bg-gray-100 text-gray-500" },
  richiesto:     { cls: "bg-blue-100 text-blue-700" },
  in_attesa:     { cls: "bg-yellow-100 text-yellow-700" },
  ricevuto:      { cls: "bg-green-100 text-green-700" },
  negativo:      { cls: "bg-red-100 text-red-700" },
};

const TIPO_CFG = {
  spontanea:      { cls: "bg-purple-100 text-purple-700" },
  risposta_avviso:{ cls: "bg-indigo-100 text-indigo-700" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
      {_locate(`ppp_proposta_stato_${stato}`) ?? stato}
    </span>
  );
}

function TipoBadge({ tipo, _locate }) {
  const cfg = TIPO_CFG[tipo] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
      {_locate(`ppp_proposta_tipo_${tipo}`) ?? tipo}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function formatCurrency(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{value || "—"}</span>
    </div>
  );
}

const TABS = ["generale", "documenti", "valutazione", "fase07", "fase10", "fase11", "fase12", "fase13", "fase14"];

export default function PropostaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast } = useStateContext();
  const { can } = useAuthUser();
  usePppAiContext('ppp.proposte', { id });
  const api = usePppApi();

  const [proposta, setProposta]         = useState(null);
  const [procedimento, setProcedimento] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState("generale");
  const [valutaForm, setValutaForm]             = useState(null);
  const [valutaIpForm, setValutaIpForm]         = useState(null);
  const [dichiaraForm, setDichiaraForm]         = useState(null);
  const [saving, setSaving]                     = useState(false);
  const [valutaErrors, setValutaErrors]                 = useState(null);
  const [valutaIpErrors, setValutaIpErrors]             = useState(null);
  const [dichiaraErrors, setDichiaraErrors]             = useState(null);
  const [pubblicaForm, setPubblicanForm]                = useState(null);
  const [pubblicaErrors, setPubblicanErrors]            = useState(null);
  const [concorrenteForm, setConcorrenteForm]           = useState(null);
  const [concorrenteErrors, setConcorrenteErrors]       = useState(null);
  const [operatoriEconomici, setOperatoriEconomici]     = useState([]);

  const load = useCallback(() => {
    api.getPropostaDiretta(id, {
      onSuccess: (data) => {
        setProposta(data);
        setLoading(false);
        // Carica il procedimento completo (con fasi) per DocumentiTab
        if (data?.procedimentoId) {
          api.getProcedimento(data.procedimentoId, {
            onSuccess: (proc) => setProcedimento(proc),
          });
        }
      },
      onError:   () => setLoading(false),
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openValuta = () => {
    setValutaForm({
      esito:            proposta?.esitoValutazione ?? "in_valutazione",
      noteValutazione:  proposta?.noteValutazione ?? "",
    });
    setValutaIpForm(null);
    setDichiaraForm(null);
    setOpenForm(true);
    setValutaErrors(null);
  };

  const handleValuta = () => {
    setSaving(true);
    setValutaErrors(null);
    api.valutaProposta(proposta.procedimentoId, proposta.id, {
      esito:           valutaForm.esito,
      noteValutazione: valutaForm.noteValutazione || null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_proposta_valutata")} />);
        setSaving(false);
        setOpenForm(false);
        load();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setValutaErrors(response.errors); },
    });
  };

  const openValutaIp = () => {
    setValutaForm(null);
    setDichiaraForm(null);
    setValutaIpForm({
      criterioCoerenzaProgrammazione: proposta.criterioCoerenzaProgrammazione ?? null,
      criterioCoerenzaDip:            proposta.criterioCoerenzaDip ?? null,
      criterioFattibilitaTecnica:     proposta.criterioFattibilitaTecnica ?? null,
      criterioSostenibilitaEconomica: proposta.criterioSostenibilitaEconomica ?? null,
      noteUfficioTecnico:             proposta.noteUfficioTecnico ?? "",
      noteUfficioAmministrativo:      proposta.noteUfficioAmministrativo ?? "",
      narsRichiesto:                  proposta.narsRichiesto ?? false,
      narsStato:                      proposta.narsStato ?? null,
      narsDataRichiesta:              proposta.narsDataRichiesta ?? "",
      narsDataRisposta:               proposta.narsDataRisposta ?? "",
      narsNote:                       proposta.narsNote ?? "",
    });
    setOpenForm(true);
    setValutaIpErrors(null);
  };

  const handleValutaIp = () => {
    setSaving(true);
    setValutaIpErrors(null);
    api.aggiornaValutazioneInteressePubblico(proposta.procedimentoId, proposta.id, {
      criterioCoerenzaProgrammazione:  valutaIpForm.criterioCoerenzaProgrammazione,
      criterioCoerenzaDip:             valutaIpForm.criterioCoerenzaDip,
      criterioFattibilitaTecnica:      valutaIpForm.criterioFattibilitaTecnica,
      criterioSostenibilitaEconomica:  valutaIpForm.criterioSostenibilitaEconomica,
      noteUfficioTecnico:              valutaIpForm.noteUfficioTecnico || null,
      noteUfficioAmministrativo:       valutaIpForm.noteUfficioAmministrativo || null,
      narsRichiesto:                   valutaIpForm.narsRichiesto,
      narsStato:                       valutaIpForm.narsStato || null,
      narsDataRichiesta:               valutaIpForm.narsDataRichiesta || null,
      narsDataRisposta:                valutaIpForm.narsDataRisposta || null,
      narsNote:                        valutaIpForm.narsNote || null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_valutazione_ip_salvata")} />);
        setSaving(false);
        setOpenForm(false);
        setValutaIpForm(null);
        load();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setValutaIpErrors(response.errors); },
    });
  };

  const openDichiara = () => {
    setDichiaraForm({ numero: "", note: "" });
    setValutaIpForm(null);
    setOpenForm(true);
    setDichiaraErrors(null);
  };

  const handleDichiara = () => {
    setSaving(true);
    setDichiaraErrors(null);
    api.dichiaraInteressePubblico(proposta.procedimentoId, proposta.id, {
      numero: dichiaraForm.numero || null,
      note:   dichiaraForm.note || null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_interesse_pubblico_dichiarato")} />);
        setSaving(false);
        setOpenForm(false);
        setDichiaraForm(null);
        load();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setDichiaraErrors(response.errors); },
    });
  };

  const openPubblica = () => {
    setPubblicanForm({ giorniTermineConcorrenti: 60, note: "" });
    setOpenForm(true);
    setPubblicanErrors(null);
  };

  const handlePubblica = () => {
    setSaving(true);
    setPubblicanErrors(null);
    api.pubblicaInteressePubblico(proposta.procedimentoId, proposta.id, {
      giorniTermineConcorrenti: parseInt(pubblicaForm.giorniTermineConcorrenti, 10) || 60,
      note: pubblicaForm.note || null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={5} content={_locate("ppp_fase07_pubblicata")} />);
        setSaving(false);
        setOpenForm(false);
        setPubblicanForm(null);
        load();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setPubblicanErrors(response.errors); },
    });
  };

  const openConcorrente = () => {
    if (operatoriEconomici.length === 0) {
      api.listOperatoriEconomici({
        onSuccess: (data) => setOperatoriEconomici(data ?? []),
      });
    }
    setConcorrenteForm({
      operatoreEconomicoId: "",
      titolo:               "",
      descrizione:          "",
      dataRicezione:        "",
      importoSpese:         "",
      note:                 "",
    });
    setOpenForm(true);
    setConcorrenteErrors(null);
  };

  const handleConcorrente = () => {
    setSaving(true);
    setConcorrenteErrors(null);
    api.registraConcorrente(proposta.procedimentoId, proposta.id, {
      operatoreEconomicoId: parseInt(concorrenteForm.operatoreEconomicoId, 10),
      titolo:               concorrenteForm.titolo,
      descrizione:          concorrenteForm.descrizione || null,
      dataRicezione:        concorrenteForm.dataRicezione || null,
      importoSpese:         concorrenteForm.importoSpese ? parseFloat(concorrenteForm.importoSpese) : null,
      note:                 concorrenteForm.note || null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_fase07_concorrente_registrata")} />);
        setSaving(false);
        setOpenForm(false);
        setConcorrenteForm(null);
        load();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setConcorrenteErrors(response.errors); },
    });
  };

  function giorniAlTermine(dateStr) {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  }

  if (loading) return <Spinner />;
  if (!proposta) return <p className="p-6 text-gray-500">{_locate("ppp_non_trovato")}</p>;

  return (
    <div className="p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate("/ppp/proposte")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <BsArrowLeft /> {_locate("ppp_proposte_torna_lista")}
          </button>
          {proposta.procedimentoId && (
            <button
              onClick={() => navigate(`/ppp/${proposta.procedimentoId}`)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {proposta.procedimentoTitolo ?? _locate("ppp_col_procedimento")} <BsBoxArrowUpRight size={12} />
            </button>
          )}
        </div>

        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight mb-1">
              {proposta.titolo}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              <StatoBadge stato={proposta.stato} _locate={_locate} />
              <TipoBadge tipo={proposta.tipoProposta} _locate={_locate} />
              {proposta.numeroProtocollo && (
                <span className="text-sm font-mono text-gray-400">
                  {_locate("ppp_proposta_prot")}: <strong className="text-gray-600">{proposta.numeroProtocollo}</strong>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end shrink-0 max-w-xs">
            <SimpleButton
              customFunc={() => window.open(api.urlRicevutaPdf(proposta.id), '_blank')}
              text={_locate("ppp_proposta_scarica_ricevuta")}
            />
            {can("ppp.proposte.evaluate") && (
              <SimpleButton customFunc={openValuta} text={_locate("ppp_proposta_valuta_btn")} />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-gray-200 mb-6 overflow-x-auto overflow-y-hidden">
        {TABS.filter((tab) => tab !== "fase12" || can("ppp.provvedimento_fase12.view")).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex-shrink-0 ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {_locate(`ppp_proposta_tab_${tab}`)}
          </button>
        ))}
      </div>

      {/* Tab Generale */}
      {activeTab === "generale" && (
        <div className="space-y-6">
          {/* Riepilogo protocollazione */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-3 uppercase tracking-wide">
              {_locate("ppp_proposta_sezione_protocollazione")}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoRow label={_locate("ppp_proposta_protocollo")} value={proposta.numeroProtocollo} />
              <InfoRow label={_locate("ppp_proposta_data_ricezione")} value={formatDate(proposta.dataRicezione)} />
              <InfoRow label={_locate("ppp_proposta_data_presentazione")} value={formatDate(proposta.dataPresentazione)} />
            </div>
          </div>

          {/* Tipo proposta */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
              {_locate("ppp_proposta_sezione_tipo")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow
                label={_locate("ppp_proposta_tipo")}
                value={_locate(`ppp_proposta_tipo_${proposta.tipoProposta}`) ?? proposta.tipoProposta}
              />
              {proposta.avvisoPubblicoOggetto && (
                <InfoRow
                  label={_locate("ppp_proposta_avviso_riferimento")}
                  value={proposta.avvisoPubblicoOggetto}
                />
              )}
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">
                {proposta.tipoProposta === "spontanea"
                  ? _locate("ppp_proposta_tipo_spontanea_normativa")
                  : _locate("ppp_proposta_tipo_risposta_normativa")}
              </p>
            </div>
          </div>

          {/* Operatore economico */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
              {_locate("ppp_proposta_sezione_operatore")}
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <InfoRow label={_locate("ppp_proposta_operatore")} value={proposta.operatoreRagioneSociale} />
              <InfoRow label="PEC" value={proposta.operatorePec} />
              <InfoRow label="Email" value={proposta.operatoreEmail} />
            </div>
          </div>

          {/* Procedimento + importo spese */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
              {_locate("ppp_proposta_sezione_dati")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label={_locate("ppp_proposta_procedimento")} value={proposta.procedimentoTitolo} />
              <InfoRow
                label={_locate("ppp_proposta_importo_spese")}
                value={formatCurrency(proposta.importoSpese)}
              />
            </div>
            {proposta.descrizione && (
              <div className="mt-3">
                <InfoRow label={_locate("ppp_proposta_descrizione")} value={proposta.descrizione} />
              </div>
            )}
            {proposta.note && (
              <div className="mt-3">
                <InfoRow label={_locate("ppp_proposta_note")} value={proposta.note} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Documenti */}
      {activeTab === "documenti" && (
        <DocumentiTab procedimento={procedimento} propostaId={proposta.id} />
      )}

      {/* Tab Valutazione */}
      {activeTab === "valutazione" && (
        <div className="space-y-4">

          {/* Criteri di valutazione (art. 193, co. 4) */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
              {_locate("ppp_valutazione_criteri_titolo")}
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["criterioCoerenzaProgrammazione", _locate("ppp_criterio_coerenza_programmazione")],
                ["criterioCoerenzaDip",            _locate("ppp_criterio_coerenza_dip")],
                ["criterioFattibilitaTecnica",     _locate("ppp_criterio_fattibilita_tecnica")],
                ["criterioSostenibilitaEconomica", _locate("ppp_criterio_sostenibilita_economica")],
              ].map(([key, label]) => {
                const val = proposta[key];
                return (
                  <div key={key} className="flex items-center gap-2">
                    <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                      val === true  ? "bg-green-100 text-green-700" :
                      val === false ? "bg-red-100 text-red-700"    :
                      "bg-gray-100 text-gray-400"
                    }`}>
                      {val === true ? "✓" : val === false ? "✗" : "—"}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </div>
                );
              })}
            </div>
            {(proposta.noteUfficioTecnico || proposta.noteUfficioAmministrativo) && (
              <div className="mt-4 grid grid-cols-2 gap-4 pt-3 border-t border-gray-100">
                {proposta.noteUfficioTecnico && (
                  <InfoRow label={_locate("ppp_note_ufficio_tecnico")} value={proposta.noteUfficioTecnico} />
                )}
                {proposta.noteUfficioAmministrativo && (
                  <InfoRow label={_locate("ppp_note_ufficio_amministrativo")} value={proposta.noteUfficioAmministrativo} />
                )}
              </div>
            )}
          </div>

          {/* Parere NARS (solo se importo >= 50M€) */}
          {proposta.richiedeParereNars && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-3 uppercase tracking-wide">
                {_locate("ppp_nars_titolo")} — art. 175, co. 3
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{_locate("ppp_nars_richiesto")}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${proposta.narsRichiesto ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                    {proposta.narsRichiesto ? _locate("ppp_si") : _locate("ppp_no")}
                  </span>
                </div>
                {proposta.narsStato && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{_locate("ppp_nars_stato")}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${(NARS_STATO_CFG[proposta.narsStato] ?? {cls:""}).cls}`}>
                      {_locate(`ppp_nars_stato_${proposta.narsStato}`) ?? proposta.narsStato}
                    </span>
                  </div>
                )}
                <InfoRow label={_locate("ppp_nars_data_richiesta")} value={formatDate(proposta.narsDataRichiesta)} />
                {proposta.narsDataRisposta && (
                  <InfoRow label={_locate("ppp_nars_data_risposta")} value={formatDate(proposta.narsDataRisposta)} />
                )}
              </div>
              {proposta.narsNote && (
                <div className="mt-3">
                  <InfoRow label={_locate("ppp_nars_note")} value={proposta.narsNote} />
                </div>
              )}
            </div>
          )}

          {/* Esito valutazione e atto */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
              {_locate("ppp_proposta_sezione_valutazione")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <InfoRow label={_locate("ppp_proposta_stato")} value={_locate(`ppp_proposta_stato_${proposta.stato}`) ?? proposta.stato} />
              {proposta.esitoValutazione && (
                <InfoRow label={_locate("ppp_proposta_esito_valutazione")} value={_locate(`ppp_proposta_stato_${proposta.esitoValutazione}`) ?? proposta.esitoValutazione} />
              )}
            </div>
            {proposta.noteValutazione && (
              <div className="mt-3">
                <InfoRow label={_locate("ppp_proposta_note_valutazione")} value={proposta.noteValutazione} />
              </div>
            )}
            {proposta.attoInteressePubblicoId && (
              <div className="mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 rounded">
                <p className="text-sm text-teal-800 dark:text-teal-200">
                  {_locate("ppp_atto_interesse_pubblico_generato")} — ID #{proposta.attoInteressePubblicoId}
                </p>
              </div>
            )}
          </div>

          {/* Azioni */}
          <div className="flex gap-2">
            {can("ppp.proposte.evaluate") && (
              <SimpleButton customFunc={openValuta} text={_locate("ppp_proposta_valuta_btn")} />
            )}
            {can("ppp.proposte.evaluate") && (
              <SimpleButton customFunc={openValutaIp} text={_locate("ppp_valutazione_ip_btn")} />
            )}
            {proposta.stato !== "interesse_pubblico_dichiarato" &&
             proposta.stato !== "ammessa" &&
             proposta.stato !== "respinta" &&
             proposta.stato !== "ritirata" &&
             can("ppp.atti.create") && (
              <LoadingButton
                isLoading={false}
                customFunc={openDichiara}
                text={_locate("ppp_dichiara_interesse_pubblico_btn")}
              />
            )}
          </div>
        </div>
      )}

      {/* Tab Fase 07 — Pubblicazione proposta e proposte concorrenti */}
      {activeTab === "fase07" && (
        <div className="space-y-4">
          {/* Banner normativo */}
          <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 rounded-lg p-4">
            <p className="text-sm text-teal-800 dark:text-teal-200 font-medium mb-1">
              {_locate("ppp_fase07_titolo")} — art. 193, co. 4 D.lgs 36/2023
            </p>
            <p className="text-xs text-teal-700 dark:text-teal-300">
              {_locate("ppp_fase07_descrizione")}
            </p>
          </div>

          {/* Stato pubblicazione */}
          {proposta.dataPubblicazioneInteressePubblico ? (
            <>
              {/* Card pubblicazione */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                  {_locate("ppp_fase07_pubblicazione_titolo")}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <InfoRow
                    label={_locate("ppp_fase07_data_pubblicazione")}
                    value={formatDate(proposta.dataPubblicazioneInteressePubblico)}
                  />
                  <InfoRow
                    label={_locate("ppp_fase07_termine_concorrenti")}
                    value={formatDate(proposta.termineProposteConcorrenti)}
                  />
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                      {_locate("ppp_fase07_giorni_rimanenti")}
                    </span>
                    {(() => {
                      const g = giorniAlTermine(proposta.termineProposteConcorrenti);
                      if (g === null) return <span className="text-sm text-gray-400">—</span>;
                      if (g < 0) return <span className="text-sm font-bold text-red-600">{_locate("ppp_scadenza_scaduta")}</span>;
                      if (g <= 10) return <span className="text-sm font-bold text-orange-600">{g} {_locate("ppp_giorni")}</span>;
                      return <span className="text-sm font-bold text-green-700">{g} {_locate("ppp_giorni")}</span>;
                    })()}
                  </div>
                </div>

                {/* Barra countdown visiva */}
                {proposta.termineProposteConcorrenti && proposta.dataPubblicazioneInteressePubblico && (() => {
                  const totale = Math.ceil((new Date(proposta.termineProposteConcorrenti) - new Date(proposta.dataPubblicazioneInteressePubblico)) / (1000 * 60 * 60 * 24));
                  const rimanenti = giorniAlTermine(proposta.termineProposteConcorrenti);
                  const perc = rimanenti !== null ? Math.max(0, Math.min(100, (rimanenti / totale) * 100)) : 0;
                  const colore = perc > 30 ? "bg-green-500" : perc > 10 ? "bg-orange-500" : "bg-red-500";
                  return (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>{formatDate(proposta.dataPubblicazioneInteressePubblico)}</span>
                        <span>{formatDate(proposta.termineProposteConcorrenti)}</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${colore}`} style={{ width: `${perc}%` }} />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Registro proposte concorrenti */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    {_locate("ppp_fase07_proponenti_titolo")} ({(proposta.proposteConcorrenti ?? []).length})
                  </h3>
                  {can("ppp.proposte.create") && (
                    <SimpleButton
                      customFunc={openConcorrente}
                      text={_locate("ppp_fase07_registra_concorrente_btn")}
                    />
                  )}
                </div>
                {(proposta.proposteConcorrenti ?? []).length === 0 ? (
                  <p className="text-sm text-gray-400 italic py-4 text-center">
                    {_locate("ppp_fase07_nessuna_proposta_concorrente")}
                  </p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {proposta.proposteConcorrenti.map((c) => (
                      <div key={c.id} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{c.operatoreRagioneSociale}</p>
                          <p className="text-xs text-gray-500 font-mono">{c.numeroProtocollo} — {c.titolo}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{formatDate(c.dataRicezione)}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${(STATO_CFG[c.stato] ?? {cls:""}).cls}`}>
                            {_locate(`ppp_proposta_stato_${c.stato}`) ?? c.stato}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Proposta non ancora pubblicata */
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
              {proposta.stato === "interesse_pubblico_dichiarato" ? (
                <>
                  <p className="text-sm text-gray-500 mb-4">
                    {_locate("ppp_fase07_non_ancora_pubblicata")}
                  </p>
                  {can("ppp.atti.create") && (
                    <div className="flex justify-center">
                      <LoadingButton
                        isLoading={false}
                        customFunc={openPubblica}
                        text={_locate("ppp_fase07_pubblica_btn")}
                      />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 italic">
                  {_locate("ppp_fase07_richiede_dichiarazione")}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab Fase 10 — Richiesta Integrazioni Documentali e Conferenza di Servizi */}
      {activeTab === "fase10" && proposta && (
        <div className="p-4">
          <Fase10Tab
            procedimentoId={proposta.procedimentoId}
            propostaId={proposta.id}
          />
        </div>
      )}

      {/* Tab Fase 11 — Valutazione Integrazioni Documentali */}
      {activeTab === "fase11" && proposta && (
        <div className="p-4">
          <Fase11Tab
            procedimentoId={proposta.procedimentoId}
            propostaId={proposta.id}
          />
        </div>
      )}

      {/* Tab Fase 12 — Provvedimento di Ammissione o Respingimento */}
      {activeTab === "fase12" && proposta && (
        <div className="p-4">
          <Fase12Tab
            procedimentoId={proposta.procedimentoId}
            propostaId={proposta.id}
            canCrea={can("ppp.provvedimento_fase12.create")}
            canGestisci={can("ppp.provvedimento_fase12.manage")}
          />
        </div>
      )}

      {/* Tab Fase 13 — Integrazione e Approvazione PFTE */}
      {activeTab === "fase13" && proposta && (
        <div className="p-4">
          <Fase13Tab
            procedimentoId={proposta.procedimentoId}
            propostaId={proposta.id}
            proposta={proposta}
            onPropostaReload={load}
          />
        </div>
      )}

      {/* Tab Fase 14 — Predisposizione Documentazione di Gara */}
      {activeTab === "fase14" && proposta && (
        <div className="p-4">
          <Fase14Tab
            procedimentoId={proposta.procedimentoId}
            proposta={proposta}
            onPropostaReload={load}
          />
        </div>
      )}

      </div>{/* fine card */}

      {/* Dialog valutazione esito */}
      {valutaForm && !valutaIpForm && !dichiaraForm && (
        <FormDialog title={_locate("ppp_proposta_valuta_titolo")} size="w-[480px]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {_locate("ppp_proposta_esito_valutazione")} *
              </label>
              <Select
                value={valutaForm.esito}
                onChange={(e) => setValutaForm((f) => ({ ...f, esito: e.target.value }))}
                options={[
                  { value: "in_valutazione", label: _locate("ppp_proposta_stato_in_valutazione") },
                  { value: "ammessa",        label: _locate("ppp_proposta_stato_ammessa") },
                  { value: "respinta",       label: _locate("ppp_proposta_stato_respinta") },
                ]}
              />
            </div>
            <TextArea
              label={_locate("ppp_proposta_note_valutazione")}
              rows={3}
              value={valutaForm.noteValutazione}
              onChange={(e) => setValutaForm((f) => ({ ...f, noteValutazione: e.target.value }))}
              placeholder={_locate("ppp_proposta_note_valutazione_hint")}
            />
            <NotificationsList errors={valutaErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handleValuta}
                text={_locate("ppp_proposta_valuta_conferma")}
              />
            </div>
          </div>
        </FormDialog>
      )}

      {/* Dialog scheda valutazione interesse pubblico */}
      {valutaIpForm && (
        <FormDialog title={_locate("ppp_valutazione_ip_titolo")} size="w-[680px]">
          <div className="space-y-5">

            {/* Criteri */}
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {_locate("ppp_valutazione_criteri_titolo")} — art. 193, co. 4
              </p>
              <div className="grid grid-cols-1 gap-2">
                {[
                  ["criterioCoerenzaProgrammazione", _locate("ppp_criterio_coerenza_programmazione")],
                  ["criterioCoerenzaDip",            _locate("ppp_criterio_coerenza_dip")],
                  ["criterioFattibilitaTecnica",     _locate("ppp_criterio_fattibilita_tecnica")],
                  ["criterioSostenibilitaEconomica", _locate("ppp_criterio_sostenibilita_economica")],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                    <Select
                      value={valutaIpForm[key] === null ? "" : String(valutaIpForm[key])}
                      onChange={(e) => setValutaIpForm((f) => ({
                        ...f,
                        [key]: e.target.value === "" ? null : e.target.value === "true",
                      }))}
                      options={[
                        { value: "",      label: _locate("ppp_criterio_non_valutato") },
                        { value: "true",  label: _locate("ppp_criterio_positivo") },
                        { value: "false", label: _locate("ppp_criterio_negativo") },
                      ]}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Note uffici */}
            <div className="grid grid-cols-2 gap-4">
              <TextArea
                label={_locate("ppp_note_ufficio_tecnico")}
                rows={3}
                value={valutaIpForm.noteUfficioTecnico}
                onChange={(e) => setValutaIpForm((f) => ({ ...f, noteUfficioTecnico: e.target.value }))}
              />
              <TextArea
                label={_locate("ppp_note_ufficio_amministrativo")}
                rows={3}
                value={valutaIpForm.noteUfficioAmministrativo}
                onChange={(e) => setValutaIpForm((f) => ({ ...f, noteUfficioAmministrativo: e.target.value }))}
              />
            </div>

            {/* NARS */}
            {proposta.richiedeParereNars && (
              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 rounded-lg">
                <p className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-3">
                  {_locate("ppp_nars_titolo")} — art. 175, co. 3
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={valutaIpForm.narsRichiesto}
                      onChange={(e) => setValutaIpForm((f) => ({ ...f, narsRichiesto: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {_locate("ppp_nars_richiesto_label")}
                    </span>
                  </label>
                  {valutaIpForm.narsRichiesto && (
                    <Select
                      label={_locate("ppp_nars_stato")}
                      value={valutaIpForm.narsStato ?? ""}
                      onChange={(e) => setValutaIpForm((f) => ({ ...f, narsStato: e.target.value || null }))}
                      options={[
                        { value: "",             label: "—" },
                        { value: "richiesto",    label: _locate("ppp_nars_stato_richiesto") },
                        { value: "in_attesa",    label: _locate("ppp_nars_stato_in_attesa") },
                        { value: "ricevuto",     label: _locate("ppp_nars_stato_ricevuto") },
                        { value: "negativo",     label: _locate("ppp_nars_stato_negativo") },
                      ]}
                    />
                  )}
                  {valutaIpForm.narsRichiesto && (
                    <>
                      <Input
                        type="date"
                        label={_locate("ppp_nars_data_richiesta")}
                        value={valutaIpForm.narsDataRichiesta}
                        onChange={(e) => setValutaIpForm((f) => ({ ...f, narsDataRichiesta: e.target.value }))}
                      />
                      <Input
                        type="date"
                        label={_locate("ppp_nars_data_risposta")}
                        value={valutaIpForm.narsDataRisposta}
                        onChange={(e) => setValutaIpForm((f) => ({ ...f, narsDataRisposta: e.target.value }))}
                      />
                    </>
                  )}
                </div>
                {valutaIpForm.narsRichiesto && (
                  <div className="mt-3">
                    <TextArea
                      label={_locate("ppp_nars_note")}
                      rows={2}
                      value={valutaIpForm.narsNote}
                      onChange={(e) => setValutaIpForm((f) => ({ ...f, narsNote: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}

            <NotificationsList errors={valutaIpErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => { setOpenForm(false); setValutaIpForm(null); }} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handleValutaIp}
                text={_locate("ppp_salva")}
              />
            </div>
          </div>
        </FormDialog>
      )}

      {/* Dialog pubblica Fase 07 */}
      {pubblicaForm && (
        <FormDialog title={_locate("ppp_fase07_pubblica_titolo")} size="w-[480px]">
          <div className="space-y-4">
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 rounded-lg">
              <p className="text-sm text-teal-800 dark:text-teal-200">
                {_locate("ppp_fase07_pubblica_avviso")}
              </p>
            </div>
            <Input
              type="number"
              label={_locate("ppp_fase07_giorni_termine_label")}
              value={pubblicaForm.giorniTermineConcorrenti}
              onChange={(e) => setPubblicanForm((f) => ({ ...f, giorniTermineConcorrenti: e.target.value }))}
              placeholder="60"
            />
            <TextArea
              label={_locate("ppp_atto_note")}
              rows={3}
              value={pubblicaForm.note}
              onChange={(e) => setPubblicanForm((f) => ({ ...f, note: e.target.value }))}
            />
            <NotificationsList errors={pubblicaErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => { setOpenForm(false); setPubblicanForm(null); }} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handlePubblica}
                text={_locate("ppp_fase07_pubblica_conferma")}
              />
            </div>
          </div>
        </FormDialog>
      )}

      {/* Dialog registra proposta concorrente */}
      {concorrenteForm && (
        <FormDialog title={_locate("ppp_fase07_registra_concorrente_titolo")} size="w-[560px]">
          <div className="space-y-4">
            <Select
              label={_locate("ppp_proposta_operatore") + " *"}
              value={concorrenteForm.operatoreEconomicoId}
              onChange={(e) => setConcorrenteForm((f) => ({ ...f, operatoreEconomicoId: e.target.value }))}
              options={[
                { value: "", label: "— " + _locate("ppp_seleziona") + " —" },
                ...operatoriEconomici.map((o) => ({ value: String(o.id), label: o.ragioneSociale })),
              ]}
            />
            <Input
              label={_locate("ppp_proposta_titolo") + " *"}
              value={concorrenteForm.titolo}
              onChange={(e) => setConcorrenteForm((f) => ({ ...f, titolo: e.target.value }))}
            />
            <TextArea
              label={_locate("ppp_proposta_descrizione")}
              rows={3}
              value={concorrenteForm.descrizione}
              onChange={(e) => setConcorrenteForm((f) => ({ ...f, descrizione: e.target.value }))}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="date"
                label={_locate("ppp_proposta_data_ricezione")}
                value={concorrenteForm.dataRicezione}
                onChange={(e) => setConcorrenteForm((f) => ({ ...f, dataRicezione: e.target.value }))}
              />
              <Input
                type="number"
                label={_locate("ppp_proposta_importo_spese")}
                value={concorrenteForm.importoSpese}
                onChange={(e) => setConcorrenteForm((f) => ({ ...f, importoSpese: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <TextArea
              label={_locate("ppp_proposta_note")}
              rows={2}
              value={concorrenteForm.note}
              onChange={(e) => setConcorrenteForm((f) => ({ ...f, note: e.target.value }))}
            />
            <NotificationsList errors={concorrenteErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => { setOpenForm(false); setConcorrenteForm(null); }} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handleConcorrente}
                text={_locate("ppp_salva")}
              />
            </div>
          </div>
        </FormDialog>
      )}

      {/* Dialog dichiara interesse pubblico */}
      {dichiaraForm && (
        <FormDialog title={_locate("ppp_dichiara_interesse_pubblico_titolo")} size="w-[480px]">
          <div className="space-y-4">
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 rounded-lg">
              <p className="text-sm text-teal-800 dark:text-teal-200">
                {_locate("ppp_dichiara_ip_avviso")}
              </p>
            </div>
            <Input
              label={_locate("ppp_atto_numero")}
              value={dichiaraForm.numero}
              onChange={(e) => setDichiaraForm((f) => ({ ...f, numero: e.target.value }))}
              placeholder="es. DET/2026/001"
            />
            <TextArea
              label={_locate("ppp_atto_note")}
              rows={3}
              value={dichiaraForm.note}
              onChange={(e) => setDichiaraForm((f) => ({ ...f, note: e.target.value }))}
            />
            <NotificationsList errors={dichiaraErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => { setOpenForm(false); setDichiaraForm(null); }} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handleDichiara}
                text={_locate("ppp_dichiara_interesse_pubblico_conferma")}
              />
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
